import { createContext } from 'react';
import type {
  Adjustment, Customer, ID, Quote, QuoteItem, QuotePreset, QuoteTotals, Service,
} from '@/types';

export interface QuoteDraft {
  customerId: ID | null;
  customerName: string;
  customerPhone: string;
  customerEmail: string;
  customerAddress: string;
  items: QuoteItem[];
  discount: Adjustment;
  taxRate: number;
  taxLabel: string;
  deposit: Adjustment;
  notes: string;
  terms: string[];
  validUntil: string;
}

export interface QuoteContextValue {
  draft: QuoteDraft;
  totals: QuoteTotals;
  quotes: Quote[];
  isLoading: boolean;
  /** The number this draft will be issued as, shown live in the preview. */
  previewNumber: string;
  /** `coalesce` merges rapid edits into a single undo step. */
  patchDraft: (patch: Partial<QuoteDraft>, options?: { coalesce?: boolean }) => void;
  applyCustomer: (customer: Customer | null) => void;
  addService: (service: Service) => void;
  /** Adds every service in the bundle, recording the bundle as their source. */
  applyPreset: (preset: QuotePreset) => { appliedCount: number; missingCount: number };
  /** Removes only what this bundle contributed; shared lines survive. */
  removePreset: (preset: QuotePreset) => { removedCount: number; keptCount: number };
  addBlankItem: () => void;
  updateItem: (id: ID, patch: Partial<QuoteItem>) => void;
  removeItem: (id: ID) => void;
  resetDraft: () => void;
  saveQuote: () => Promise<Quote>;
  deleteQuote: (id: ID) => Promise<void>;
  loadQuoteIntoDraft: (quote: Quote) => void;
  undo: () => boolean;
  redo: () => boolean;
  canUndo: boolean;
  canRedo: boolean;
  /** When the draft was last written to storage, for the autosave indicator. */
  lastSavedAt: string | null;
  isRestoredDraft: boolean;

  /* ── Derived bundle state, computed once here rather than in views ── */
  activePresetIds: ReadonlySet<ID>;
  /** Bundle id → number of quote lines it currently contributes to. */
  presetCounts: Readonly<Record<ID, number>>;
  /** Catalog lines the user picked themselves — reporting only. */
  manualLineCount: number;
  /** Unique catalog services on the quote, whatever their origin. */
  catalogLineCount: number;
  /** The saved bundle this quote already matches exactly, if any. */
  matchedPreset: QuotePreset | null;
}

export const QuoteContext = createContext<QuoteContextValue | null>(null);
